/**
 * ProductSelector - Component for selecting multiple products with quantity and optional price
 */
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Autocomplete,
  TextField,
  Box,
  IconButton,
  Tooltip,
  Typography,
  Paper,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon, Close as CloseIcon, CallSplit as CallSplitIcon } from '@mui/icons-material';
import { useQuery, useQueries } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { productsApi, ProductApiResponse } from '../../services/productsApi';
import { bundlesApi, BundleDefinition } from '../../services/bundlesApi';
import { getTextFieldStyles, getAutocompleteStyles, getAutocompleteListboxStyles } from '../../styles/formStyles';
import { useWarehouse } from '../../contexts/WarehouseContext';
import { LabelSelector } from '../Labels';
import CategorySelector from '../CategorySelector/CategorySelector';
import Table, { Column } from '../Table/Table';
import { useServerSidePagination } from '../../hooks/useServerSidePagination';
import { PaginatedResponse } from '../../types/api';
import {
  getStorageLocations,
  getProductLocations,
  StorageLocationOption,
  suggestLocations,
} from '../../services/locationApi';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import type { ProductLocationsDetail } from '../WarehouseMap/pathTypes';

/** Opcja lokalizacji dla dokumentów z wyborem półki: etykieta z kontekstem stanu */
export type KorStorageOption = StorageLocationOption & { korLabel: string };
const MS_IN_DAY = 24 * 60 * 60 * 1000;

function dateInputToDays(value: string): number | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const parsedDate = new Date(trimmed);
  if (Number.isNaN(parsedDate.getTime())) return undefined;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  parsedDate.setHours(0, 0, 0, 0);
  const diffDays = Math.ceil((parsedDate.getTime() - today.getTime()) / MS_IN_DAY);
  return diffDays > 0 ? diffDays : undefined;
}

function buildKorStorageOptions(
  quantity: number,
  detail: ProductLocationsDetail | undefined,
  allStorage: StorageLocationOption[],
  t: (key: string, opt?: Record<string, unknown>) => string,
  onlyLocationsWithStock: boolean
): KorStorageOption[] {
  const hasAllStorage = allStorage.length > 0;
  const allowedIds = hasAllStorage ? new Set(allStorage.map((s) => s.id)) : null;

  if (onlyLocationsWithStock) {
    if (!detail?.locations?.length) return [];
    return detail.locations
      .filter((e) => (
        (!allowedIds || allowedIds.has(e.location.id)) &&
        e.availableQuantity > 0 &&
        e.availableQuantity >= Math.max(quantity, 1)
      ))
      .map((e) => {
        const base = allStorage.find((s) => s.id === e.location.id);
        const rackPrefix = e.rack?.code ? `${e.rack.code} › ` : '';
        const path = base?.full_location_code || `${rackPrefix}${e.location.code}`;
        const korLabel = `${path} (${t('documents.kor.availableQty', { count: e.availableQuantity })})`;
        return {
          ...(base ?? {
            id: e.location.id,
            code: e.location.code,
            name: e.location.name,
            location_type: e.location.type,
            status: 'ACTIVE',
            warehouse: 0,
          }),
          full_location_code: base?.full_location_code || path,
          korLabel,
        };
      });
  }

  if (!allStorage.length) return [];

  if (quantity < 0) {
    if (!detail?.locations?.length) return [];
    return detail.locations
      .filter((e) => (!allowedIds || allowedIds.has(e.location.id)) && e.availableQuantity > 0)
      .map((e) => {
        const base = allStorage.find((s) => s.id === e.location.id);
        const rackPrefix = e.rack?.code ? `${e.rack.code} › ` : '';
        const path = base?.full_location_code || `${rackPrefix}${e.location.code}`;
        const korLabel = `${path} (${t('documents.kor.availableQty', { count: e.availableQuantity })})`;
        return {
          ...(base ?? {
            id: e.location.id,
            code: e.location.code,
            name: e.location.name,
            location_type: e.location.type,
            status: 'ACTIVE',
            warehouse: 0,
          }),
          full_location_code: base?.full_location_code || path,
          korLabel,
        };
      });
  }

  const stockByLocId = new Map<number, number>();
  detail?.locations.forEach((e) => {
    stockByLocId.set(e.location.id, e.availableQuantity);
  });

  return allStorage.map((sl) => {
    const n = stockByLocId.get(sl.id) ?? 0;
    const base = sl.full_location_code || `${sl.code} · ${sl.name}`;
    const korLabel = `${base} (${t('documents.kor.qtyInParens', { count: n })})`;
    return { ...sl, korLabel };
  });
}

export interface ProductSelection {
  product_id?: number; // dla istniejących produktów
  product_name?: string; // z dokumentu (np. awizacja) gdy produkt nie jest w API
  product_code?: string;
  /** Lokalizacja magazynowa (półka/slot) — wymagana m.in. dla KOR */
  storage_location_id?: number;
  storage_location_code?: string;
  source_storage_location_id?: number;
  source_storage_location_code?: string;
  product?: {          // dla nowych produktów
    name: string;
    description?: string;
    category: string[];
    price: number | string;
    supplier_ids?: number[];
    label_ids?: number[];
    shelf_life_days?: number | null;
    expiry_warning_days?: number | null;
    block_expired_sales?: boolean;
  };
  quantity: number;
  unit_price?: string;
}

interface ProductSelectorProps {
  value: ProductSelection[];
  onChange: (products: ProductSelection[]) => void;
  showPrice?: boolean;
  disabled?: boolean;
  label?: string;
  error?: boolean;
  helperText?: string;
  required?: boolean;
  showAddNewButton?: boolean;
  useTableSelector?: boolean;
  allowNegativeQuantity?: boolean;
  /** Dla KOR: wymusza wybór lokalizacji magazynowej przy każdej pozycji */
  requireStorageLocation?: boolean;
  /** Ukryj lokalizacje tymczasowe (temp=true), np. dla WZ. */
  excludeTempLocations?: boolean;
  /** Allow adding the same existing product multiple times (for PP split). */
  allowDuplicateProducts?: boolean;
  /** Require explicit source location ("skad"), e.g. for PP. */
  requireSourceStorageLocation?: boolean;
  /** Show dedicated bundle picker button/dialog (e.g. KOR, PP). */
  showBundleSelector?: boolean;
}

const ProductSelector: React.FC<ProductSelectorProps> = ({
  value = [],
  onChange,
  showPrice = false,
  disabled = false,
  label,
  error = false,
  helperText,
  required = false,
  showAddNewButton = true,
  useTableSelector = false,
  allowNegativeQuantity = false,
  requireStorageLocation = false,
  excludeTempLocations = false,
  allowDuplicateProducts = false,
  requireSourceStorageLocation = false,
  showBundleSelector = false,
}) => {
  const { t } = useTranslation();
  const { selectedWarehouse, loading: warehousesLoading } = useWarehouse();
  const [selectedProducts, setSelectedProducts] = useState<ProductApiResponse[]>([]);
  const [newProducts, setNewProducts] = useState<Array<{ id: string; product: ProductSelection['product']; quantity: number; unit_price?: string }>>([]);
  const [productQuantities, setProductQuantities] = useState<Record<string, number>>({}); // key can be product_id or new product id
  // Surowy, wyświetlany tekst w polu "Ilość" — pozwala pisać stany pośrednie jak "", "-", "-1" bez ich natychmiastowej konwersji na 0.
  const [productQuantityInputs, setProductQuantityInputs] = useState<Record<string, string>>({});
  const [productPrices, setProductPrices] = useState<Record<string, string>>({}); // key can be product_id or new product id
  const [productStorageLocations, setProductStorageLocations] = useState<Record<string, number | null>>({});
  const [productSourceStorageLocations, setProductSourceStorageLocations] = useState<Record<string, number | null>>({});

  // Refs to store current state values for use in callbacks
  const selectedProductsRef = useRef<ProductApiResponse[]>([]);
  const newProductsRef = useRef<Array<{ id: string; product: ProductSelection['product']; quantity: number; unit_price?: string }>>([]);
  const productQuantitiesRef = useRef<Record<string, number>>({});
  const productPricesRef = useRef<Record<string, string>>({});
  const productStorageLocationsRef = useRef<Record<string, number | null>>({});
  const productSourceStorageLocationsRef = useRef<Record<string, number | null>>({});

  // Keep refs in sync with state
  useEffect(() => {
    selectedProductsRef.current = selectedProducts;
  }, [selectedProducts]);

  useEffect(() => {
    newProductsRef.current = newProducts;
  }, [newProducts]);

  useEffect(() => {
    productQuantitiesRef.current = productQuantities;
  }, [productQuantities]);

  useEffect(() => {
    productPricesRef.current = productPrices;
  }, [productPrices]);

  useEffect(() => {
    productStorageLocationsRef.current = productStorageLocations;
  }, [productStorageLocations]);
  useEffect(() => {
    productSourceStorageLocationsRef.current = productSourceStorageLocations;
  }, [productSourceStorageLocations]);

  const { data: storageLocationOptions = [], isLoading: storageLocationsLoading } = useQuery({
    queryKey: ['storageLocations', selectedWarehouse?.id, requireStorageLocation],
    queryFn: () => getStorageLocations(selectedWarehouse!.id),
    enabled:
      !warehousesLoading &&
      !!selectedWarehouse?.id &&
      requireStorageLocation &&
      // For flows using only locations with stock (e.g. PP), product_locations is enough.
      // Fetch full storage list only when needed for global destination choices or temp exclusion.
      (allowNegativeQuantity || excludeTempLocations || requireSourceStorageLocation),
  });

  const visibleStorageLocationOptions = useMemo(
    () => (
      excludeTempLocations
        ? storageLocationOptions.filter((loc) => !loc.temp)
        : storageLocationOptions
    ),
    [excludeTempLocations, storageLocationOptions]
  );

  const productLocationQueries = useQueries({
    queries: selectedProducts.map((product) => ({
      queryKey: ['productLocations', product.id, selectedWarehouse?.id],
      queryFn: () => getProductLocations(product.id, selectedWarehouse!.id),
      enabled: !warehousesLoading && !!selectedWarehouse?.id && requireStorageLocation,
    })),
  });

  const [newProductDialogOpen, setNewProductDialogOpen] = useState(false);
  const [tableSelectorDialogOpen, setTableSelectorDialogOpen] = useState(false);
  const [bundleSelectorDialogOpen, setBundleSelectorDialogOpen] = useState(false);
  const [selectingBundleId, setSelectingBundleId] = useState<number | null>(null);
  const [newProductFormData, setNewProductFormData] = useState<{
    name: string;
    description: string;
    category: string[];
    price: string;
    quantity: number;
    unit_price: string;
    supplier_ids: number[];
    label_ids: number[];
    shelf_life_days: string;
  }>({
    name: '',
    description: '',
    category: [],
    price: '',
    quantity: 1,
    unit_price: '',
    supplier_ids: [],
    label_ids: [],
    shelf_life_days: '',
  });

  const makeLineKey = () => `line-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const getProductLineKey = (product: ProductApiResponse, fallbackIndex?: number) => {
    const p = product as ProductApiResponse & { _lineKey?: string };
    return p._lineKey || (fallbackIndex != null ? `${product.id}-${fallbackIndex}` : String(product.id));
  };

  // Fetch all products (for Autocomplete mode)
  const { data: productsData, isLoading } = useQuery({
    queryKey: ['products', selectedWarehouse?.id],
    queryFn: () => productsApi.getProducts({
      page_size: 1000,
      warehouse: selectedWarehouse?.id
    }),
    enabled: !warehousesLoading && !!selectedWarehouse?.id && !useTableSelector,
  });

  const products = useMemo(() => productsData?.results || [], [productsData?.results]);

  // Fetch products for Table selector (server-side pagination)
  const fetchProductsForTable = React.useCallback(
    async (
      page: number,
      pageSize: number,
      search: string
    ): Promise<PaginatedResponse<ProductApiResponse>> => {
      if (warehousesLoading || !selectedWarehouse?.id) {
        return {
          count: 0,
          next: null,
          previous: null,
          results: [],
        };
      }

      const response = await productsApi.getProducts({
        page: page + 1,
        page_size: pageSize,
        search: search || undefined,
        warehouse: selectedWarehouse.id,
      });
      return response;
    },
    [selectedWarehouse?.id, warehousesLoading]
  );

  const tablePagination = useServerSidePagination(fetchProductsForTable, {
    initialPageSize: 10,
    debounceMs: 500,
  });

  const { data: bundleDefinitions = [], isLoading: bundleDefinitionsLoading } = useQuery({
    queryKey: ['bundleDefinitions', selectedWarehouse?.id, showBundleSelector],
    queryFn: () => bundlesApi.getDefinitions({ warehouse: selectedWarehouse?.id }),
    enabled: useTableSelector && showBundleSelector && bundleSelectorDialogOpen && !!selectedWarehouse?.id,
  });
  const bundleDefinitionsOnStock = useMemo(
    () => bundleDefinitions.filter((bundle) => (
      (bundle.total_assembled ?? 0) > 0 && (bundle.bundle_product_quantity ?? 0) > 0
    )),
    [bundleDefinitions]
  );

  // Fetch individual products by ID when in table selector mode (full list not loaded)
  // If value contains product_name/product_code (from document, e.g. awizacja), use those instead of API
  const fetchedProductIdsRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    if (!useTableSelector) return;
    if (!value || value.length === 0) return;

    const existingProductIds = value
      .filter(v => v.product_id)
      .map(v => v.product_id!);

    if (existingProductIds.length === 0) return;

    const missingIds = existingProductIds.filter(
      id => !fetchedProductIdsRef.current.has(id) &&
        !selectedProductsRef.current.some(p => p.id === id)
    );

    if (missingIds.length === 0) return;

    // Build minimal product from document data (e.g. awizacja) when API would return 404
    const valueById = new Map(
      value.filter(v => v.product_id).map(v => [v.product_id!, v])
    );
    const fromValue: ProductApiResponse[] = [];
    const idsToFetch: number[] = [];
    for (const id of missingIds) {
      const v = valueById.get(id);
      if (v && (v.product_name != null && v.product_name !== '')) {
        fromValue.push({
          id,
          name: v.product_name,
          product_code: v.product_code ?? '',
          price: typeof v.unit_price === 'string' ? v.unit_price : String(v.unit_price ?? '0'),
          quantity: 0,
          created_at: '',
          updated_at: '',
          created_by: 0,
          created_by_name: '',
          updated_by: 0,
          updated_by_name: '',
          category: [],
          suppliers: [],
        });
        fetchedProductIdsRef.current.add(id);
      } else {
        idsToFetch.push(id);
      }
    }

    if (fromValue.length > 0) {
      setSelectedProducts(prev => {
        const existingIds = new Set(prev.map(p => p.id));
        const newOnes = fromValue.filter(p => !existingIds.has(p.id));
        return newOnes.length > 0 ? [...prev, ...newOnes] : prev;
      });
    }

    if (idsToFetch.length === 0) return;

    let cancelled = false;

    const fetchMissing = async () => {
      try {
        const results = await Promise.allSettled(
          idsToFetch.map(id => productsApi.getProduct(id))
        );
        if (cancelled) return;
        const fetched: ProductApiResponse[] = [];
        results.forEach((result, i) => {
          const id = idsToFetch[i];
          if (result.status === 'fulfilled') {
            fetched.push(result.value);
            fetchedProductIdsRef.current.add(id);
          }
        });
        if (fetched.length > 0) {
          setSelectedProducts(prev => {
            const existingIds = new Set(prev.map(p => p.id));
            const newOnes = fetched.filter(p => !existingIds.has(p.id));
            return newOnes.length > 0 ? [...prev, ...newOnes] : prev;
          });
        }
      } catch (error) {
        console.error('Error fetching products for edit mode:', error);
      }
    };

    fetchMissing();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useTableSelector, value]);

  // Track previous value to avoid unnecessary updates
  const prevValueRef = useRef<string>('');
  // Track if update is coming from internal state change (not from parent)
  const isInternalUpdateRef = useRef<boolean>(false);

  // Create a stable string representation of value for comparison
  const valueKey = useMemo(() => {
    if (!value || value.length === 0) return '';
    return JSON.stringify(value.map(v => ({
      product_id: v.product_id,
      quantity: v.quantity,
      unit_price: v.unit_price,
      product_name: v.product_name ?? v.product?.name,
      product_code: v.product_code,
      storage_location_id: v.storage_location_id,
      source_storage_location_id: v.source_storage_location_id,
    })).sort((a, b) => (a.product_id || 0) - (b.product_id || 0)));
  }, [value]);

  // Initialize state from value prop
  useEffect(() => {
    // Skip if value hasn't actually changed
    if (prevValueRef.current === valueKey) {
      return;
    }

    // Skip if this is an internal update (we're syncing our state to parent)
    if (isInternalUpdateRef.current) {
      isInternalUpdateRef.current = false;
      prevValueRef.current = valueKey;
      return;
    }

    prevValueRef.current = valueKey;

    if (value && value.length > 0) {
      // Separate existing products from new products
      const existingProductsValue = value.filter((v) => v.product_id);
      const newProductsValue = value.filter((v) => v.product && !v.product_id);

      if (products.length > 0 && existingProductsValue.length > 0) {
        const selected = existingProductsValue
          .map((v, idx) => {
            const found = products.find((product) => product.id === v.product_id);
            if (!found) return null;
            return allowDuplicateProducts
              ? ({ ...found, _lineKey: makeLineKey() } as ProductApiResponse)
              : found;
          })
          .filter((p): p is ProductApiResponse => p !== null);
        setSelectedProducts(selected);
      } else if (existingProductsValue.length > 0 && products.length === 0) {
        // Products not loaded yet, but we have values - keep current selection
        // Don't reset, wait for products to load
      } else {
        setSelectedProducts([]);
      }

      // Initialize new products
      if (newProductsValue.length > 0) {
        const mappedNewProducts = newProductsValue.map((v, index) => ({
          id: `new-${Date.now()}-${index}`,
          product: v.product!,
          quantity: v.quantity,
          unit_price: v.unit_price,
        }));
        setNewProducts(mappedNewProducts);
      } else {
        setNewProducts([]);
      }

      const quantities: Record<string, number> = {};
      const prices: Record<string, string> = {};
      let existingIdx = 0;
      value.forEach((v, index) => {
        const key = v.product_id
          ? (
            allowDuplicateProducts
              ? getProductLineKey((selectedProducts[existingIdx++] as ProductApiResponse) || ({ id: v.product_id } as ProductApiResponse), existingIdx - 1)
              : String(v.product_id)
          )
          : `new-${index}`;
        quantities[key] = v.quantity;
        if (v.unit_price !== undefined) {
          prices[key] = v.unit_price;
        }
      });
      setProductQuantities(quantities);
      setProductPrices(prices);

      const slocs: Record<string, number | null> = {};
      const srcSlocs: Record<string, number | null> = {};
      let existingSIdx = 0;
      value.forEach((v, index) => {
        if (v.product_id) {
          const key = allowDuplicateProducts
            ? getProductLineKey((selectedProducts[existingSIdx++] as ProductApiResponse) || ({ id: v.product_id } as ProductApiResponse), index)
            : String(v.product_id);
          slocs[key] = v.storage_location_id ?? null;
          srcSlocs[key] = v.source_storage_location_id ?? null;
        }
      });
      setProductStorageLocations(slocs);
      productStorageLocationsRef.current = slocs;
      setProductSourceStorageLocations(srcSlocs);
      productSourceStorageLocationsRef.current = srcSlocs;
    } else {
      setSelectedProducts([]);
      setNewProducts([]);
      setProductQuantities({});
      setProductPrices({});
      setProductStorageLocations({});
      productStorageLocationsRef.current = {};
      setProductSourceStorageLocations({});
      productSourceStorageLocationsRef.current = {};
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueKey, products, value]);

  const korOptionsByKey = useMemo(() => {
    const map = new Map<string, KorStorageOption[]>();
    const needsAllStorageList = allowNegativeQuantity || excludeTempLocations;
    if (!requireStorageLocation || (needsAllStorageList && !visibleStorageLocationOptions.length)) {
      return map;
    }
    // For PP (explicit source+target flow), destination should allow all active locations.
    // Stock-based restriction stays only for source selector.
    const onlyLocationsWithStock = !allowNegativeQuantity && !requireSourceStorageLocation;

    selectedProducts.forEach((product, i) => {
      const key = getProductLineKey(product, i);
      const qty = productQuantities[key] ?? 0;
      const detail = productLocationQueries[i]?.data as ProductLocationsDetail | undefined;
      map.set(key, buildKorStorageOptions(qty, detail, visibleStorageLocationOptions, t, onlyLocationsWithStock));
    });

    newProducts.forEach((np) => {
      const key = np.id;
      const qty = productQuantities[key] ?? 0;
      map.set(key, buildKorStorageOptions(qty, undefined, visibleStorageLocationOptions, t, onlyLocationsWithStock));
    });

    return map;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    requireStorageLocation,
    visibleStorageLocationOptions,
    selectedProducts,
    newProducts,
    productQuantities,
    productLocationQueries,
    t,
    allowNegativeQuantity,
  ]);

  const sourceOptionsByKey = useMemo(() => {
    const map = new Map<string, KorStorageOption[]>();
    if (!requireSourceStorageLocation) return map;
    selectedProducts.forEach((product, i) => {
      const key = getProductLineKey(product, i);
      const qty = Math.max(Number(productQuantities[key] ?? 0), 1);
      const detail = productLocationQueries[i]?.data as ProductLocationsDetail | undefined;
      const opts = buildKorStorageOptions(qty, detail, visibleStorageLocationOptions, t, true);

      const selectedSourceId = productSourceStorageLocationsRef.current[key];
      if (selectedSourceId != null && !opts.find((o) => o.id === selectedSourceId)) {
        const base = visibleStorageLocationOptions.find((s) => s.id === selectedSourceId);
        if (base) {
          const locDetail = detail?.locations?.find((e) => e.location.id === selectedSourceId);
          const available = locDetail?.availableQuantity ?? 0;
          const path = base.full_location_code || base.code;
          const korLabel = `${path} (${t('documents.kor.availableQty', { count: available })} - brak wystarczającego stanu)`;
          opts.push({
            ...base,
            korLabel,
          });
        }
      }

      map.set(key, opts);
    });
    return map;
  }, [
    requireSourceStorageLocation,
    selectedProducts,
    productQuantities,
    productLocationQueries,
    visibleStorageLocationOptions,
    t,
  ]);

  const storageLocationHint = useMemo(() => {
    if (allowNegativeQuantity) {
      return t('documents.kor.storageLocationHint');
    }
    return t('documents.wz.storageLocationHint', {
      defaultValue: 'Najpierw wpisz ilość: wybór tylko z lokalizacji, gdzie jest wystarczający stan.',
    });
  }, [allowNegativeQuantity, t]);

  const shouldShowStorageLocationHint = useMemo(
    () => (
      requireStorageLocation &&
      (
        newProducts.length > 0 ||
        selectedProducts.some((product) => !product.is_bundle)
      )
    ),
    [newProducts.length, requireStorageLocation, selectedProducts]
  );

  // Track previous parent update to avoid unnecessary onChange calls
  const prevParentUpdateRef = useRef<string>('');

  // Sync state changes back to parent
  const updateParent = useCallback((
    newSelected: ProductApiResponse[],
    newNewProducts: Array<{ id: string; product: ProductSelection['product']; quantity: number; unit_price?: string }>,
    newQuantities: Record<string, number>,
    newPrices: Record<string, string>,
    newStorageLocs?: Record<string, number | null>
  ) => {
    const storageMap = newStorageLocs ?? productStorageLocationsRef.current;
    const sourceStorageMap = productSourceStorageLocationsRef.current;

    const resolveStorageCode = (lineKey: string, slId: number): string | undefined => {
      const korOpts = korOptionsByKey.get(lineKey);
      const fromKor = korOpts?.find((o) => o.id === slId);
      if (fromKor) {
        return fromKor.full_location_code || fromKor.code;
      }
      const fromAll = visibleStorageLocationOptions.find((o) => o.id === slId);
      return fromAll?.full_location_code || fromAll?.code;
    };

    const productSelections: ProductSelection[] = [
      // Existing products
      ...newSelected.map((product, i) => {
        const key = getProductLineKey(product, i);
        const unitPrice = showPrice && newPrices[key]
          ? newPrices[key]
          : product.price || '0.00';
        const slId = storageMap[key];
        const srcSlId = sourceStorageMap[key];
        const code = requireStorageLocation && slId != null ? resolveStorageCode(key, slId) : undefined;
        const srcCode = requireSourceStorageLocation && srcSlId != null ? resolveStorageCode(key, srcSlId) : undefined;

        return {
          product_id: product.id,
          quantity: allowNegativeQuantity && product.is_bundle
            ? Math.min(newQuantities[key] || 0, -1)
            : (newQuantities[key] || 0),
          unit_price: unitPrice,
          ...(product.name && { product_name: product.name }),
          ...(product.product_code !== undefined && product.product_code !== '' && { product_code: product.product_code }),
          ...(requireStorageLocation && slId != null && { storage_location_id: slId }),
          ...(code && { storage_location_code: code }),
          ...(requireSourceStorageLocation && srcSlId != null && { source_storage_location_id: srcSlId }),
          ...(srcCode && { source_storage_location_code: srcCode }),
        };
      }),
      // New products
      ...newNewProducts
        .filter((newProduct) => newProduct.product)
        .map((newProduct) => {
          const nk = newProduct.id;
          const slId = storageMap[nk];
          const srcSlId = sourceStorageMap[nk];
          const code = requireStorageLocation && slId != null ? resolveStorageCode(nk, slId) : undefined;
          const srcCode = requireSourceStorageLocation && srcSlId != null ? resolveStorageCode(nk, srcSlId) : undefined;
          return {
            product: newProduct.product!,
            quantity: newQuantities[nk] || newProduct.quantity || 0,
            unit_price: (newPrices[nk] || newProduct.unit_price || String(newProduct.product!.price) || '0.00'),
            ...(requireStorageLocation && slId != null && { storage_location_id: slId }),
            ...(code && { storage_location_code: code }),
            ...(requireSourceStorageLocation && srcSlId != null && { source_storage_location_id: srcSlId }),
            ...(srcCode && { source_storage_location_code: srcCode }),
          };
        }),
    ].filter((p) => allowNegativeQuantity ? p.quantity !== 0 : p.quantity > 0);

    // Create a stable string representation for comparison
    const updateKey = JSON.stringify(productSelections.map((p, idx) => ({
      idx,
      product_id: p.product_id,
      quantity: p.quantity,
      unit_price: p.unit_price,
      product_name: p.product_name ?? p.product?.name,
      product_code: p.product_code,
      storage_location_id: p.storage_location_id,
      source_storage_location_id: p.source_storage_location_id,
    })));

    // Only call onChange if the value actually changed
    if (prevParentUpdateRef.current !== updateKey) {
      prevParentUpdateRef.current = updateKey;
      // Mark this as internal update to prevent useEffect from overwriting our changes
      isInternalUpdateRef.current = true;
      onChange(productSelections);
    }
  }, [onChange, showPrice, allowNegativeQuantity, requireStorageLocation, requireSourceStorageLocation, visibleStorageLocationOptions, korOptionsByKey]);

  // Czyść wybór lokalizacji, gdy po zmianie ilości / stanów opcja nie jest już dostępna
  useEffect(() => {
    if (!requireStorageLocation || storageLocationsLoading) return;

    const newStorage = { ...productStorageLocationsRef.current };
    let changed = false;

    const tryClear = (lineKey: string, productRowIndex: number | null) => {
      const slId = newStorage[lineKey];
      if (slId == null) return;
      if (productRowIndex !== null && productLocationQueries[productRowIndex]?.isLoading) {
        return;
      }
      const opts = korOptionsByKey.get(lineKey) ?? [];
      if (opts.length === 0 || !opts.some((o) => o.id === slId)) {
        newStorage[lineKey] = null;
        changed = true;
      }
    };

    selectedProducts.forEach((product, i) => {
      tryClear(getProductLineKey(product, i), i);
    });
    newProducts.forEach((np) => {
      tryClear(np.id, null);
    });

    if (changed) {
      productStorageLocationsRef.current = newStorage;
      setProductStorageLocations(newStorage);
      updateParent(
        selectedProductsRef.current,
        newProductsRef.current,
        productQuantitiesRef.current,
        productPricesRef.current,
        newStorage
      );
    }
  }, [
    requireStorageLocation,
    storageLocationsLoading,
    korOptionsByKey,
    selectedProducts,
    newProducts,
    productLocationQueries,
    updateParent,
  ]);

  const handleProductChange = (_event: any, newValue: ProductApiResponse[]) => {
    setSelectedProducts(newValue);

    // Initialize quantities and prices for new products
    const newQuantities = { ...productQuantitiesRef.current };
    const newPrices = { ...productPricesRef.current };

    newValue.forEach((product, i) => {
      const key = getProductLineKey(product, i);
      if (!(key in newQuantities)) {
        newQuantities[key] = allowNegativeQuantity && product.is_bundle ? -1 : 1;
      }
      if (showPrice && !(key in newPrices)) {
        newPrices[key] = product.price || '0.00';
      }
    });

    // Remove quantities/prices for removed products (only for existing products)
    const selectedIds = new Set(newValue.map((p, i) => getProductLineKey(p, i)));
    Object.keys(newQuantities).forEach((key) => {
      if (key.startsWith('new-')) return;
      if (!selectedIds.has(key)) {
        delete newQuantities[key];
        delete newPrices[key];
      }
    });

    const newStorage: Record<string, number | null> = {};
    const newSourceStorage: Record<string, number | null> = {};
    newValue.forEach((product, i) => {
      const k = getProductLineKey(product, i);
      newStorage[k] = productStorageLocationsRef.current[k] ?? null;
      newSourceStorage[k] = productSourceStorageLocationsRef.current[k] ?? null;
    });
    productStorageLocationsRef.current = newStorage;
    setProductStorageLocations(newStorage);
    productSourceStorageLocationsRef.current = newSourceStorage;
    setProductSourceStorageLocations(newSourceStorage);

    setProductQuantities(newQuantities);
    setProductPrices(newPrices);
    updateParent(newValue, newProductsRef.current, newQuantities, newPrices, newStorage);
  };

  const handleStorageLocationChange = (key: string, option: KorStorageOption | null) => {
    const newStorage = { ...productStorageLocationsRef.current, [key]: option?.id ?? null };
    productStorageLocationsRef.current = newStorage;
    setProductStorageLocations(newStorage);
    updateParent(
      selectedProductsRef.current,
      newProductsRef.current,
      productQuantitiesRef.current,
      productPricesRef.current,
      newStorage
    );
  };

  const handleSourceStorageLocationChange = (key: string, option: KorStorageOption | null) => {
    const newStorage = { ...productSourceStorageLocationsRef.current, [key]: option?.id ?? null };
    productSourceStorageLocationsRef.current = newStorage;
    setProductSourceStorageLocations(newStorage);
    updateParent(
      selectedProductsRef.current,
      newProductsRef.current,
      productQuantitiesRef.current,
      productPricesRef.current
    );
  };

  const handleQuantityChange = (key: string, raw: string, isBundleProduct = false) => {
    // Dopuszczamy tylko cyfry, opcjonalny minus (KOR) i kropkę dziesiętną.
    const pattern = allowNegativeQuantity ? /^-?\d*\.?\d*$/ : /^\d*\.?\d*$/;
    if (raw !== '' && !pattern.test(raw)) {
      return;
    }

    // Stany pośrednie ("", "-", ".") traktujemy jako 0 w wartości numerycznej,
    // ale nie nadpisujemy tekstu w polu, żeby użytkownik mógł dokończyć pisać.
    let numValue = 0;
    let nextRaw = raw;
    const isIntermediate = raw === '' || raw === '-' || raw === '.' || raw === '-.';
    if (!isIntermediate) {
      const parsed = parseFloat(raw);
      numValue = Number.isNaN(parsed) ? 0 : parsed;
      // W KOR pakiet można tylko zdejmować (montaż odpowiada za dodawanie).
      if (allowNegativeQuantity && isBundleProduct && numValue > -1) {
        numValue = -1;
        nextRaw = '-1';
      }
    }
    setProductQuantityInputs((prev) => ({ ...prev, [key]: nextRaw }));

    const newQuantities = { ...productQuantitiesRef.current, [key]: numValue };
    setProductQuantities(newQuantities);

    updateParent(
      selectedProductsRef.current,
      newProductsRef.current,
      newQuantities,
      productPricesRef.current
    );
  };

  // Po opuszczeniu pola: dokończ niekompletny input (np. "-", "") — dla trybu bez ujemnych
  // wymuszamy minimalną 1, dla KOR puste pole zamieniamy na 0.
  const handleQuantityBlur = (key: string, isBundleProduct = false) => {
    const raw = productQuantityInputs[key];
    if (raw === undefined) return;
    const isIntermediate = raw === '' || raw === '-' || raw === '.' || raw === '-.';
    if (!isIntermediate) return;

    const fallback = allowNegativeQuantity ? (isBundleProduct ? -1 : 0) : 1;
    setProductQuantityInputs((prev) => ({ ...prev, [key]: String(fallback) }));
    const newQuantities = { ...productQuantitiesRef.current, [key]: fallback };
    setProductQuantities(newQuantities);
    updateParent(
      selectedProductsRef.current,
      newProductsRef.current,
      newQuantities,
      productPricesRef.current
    );
  };

  const handlePriceChange = (key: string, price: string) => {
    const newPrices = { ...productPricesRef.current, [key]: price };
    setProductPrices(newPrices);

    // Update parent with all current values from refs
    updateParent(
      selectedProductsRef.current,
      newProductsRef.current,
      productQuantitiesRef.current,
      newPrices
    );
  };

  const handleRemoveProduct = (key: string) => {
    setProductQuantityInputs((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    if (key.startsWith('new-')) {
      // Remove new product
      const newNewProducts = newProductsRef.current.filter((p) => p.id !== key);
      setNewProducts(newNewProducts);
      const newQuantities = { ...productQuantitiesRef.current };
      const newPrices = { ...productPricesRef.current };
      const newStorage = { ...productStorageLocationsRef.current };
      const newSourceStorage = { ...productSourceStorageLocationsRef.current };
      delete newQuantities[key];
      delete newPrices[key];
      delete newStorage[key];
      delete newSourceStorage[key];
      productStorageLocationsRef.current = newStorage;
      setProductStorageLocations(newStorage);
      productSourceStorageLocationsRef.current = newSourceStorage;
      setProductSourceStorageLocations(newSourceStorage);
      setProductQuantities(newQuantities);
      setProductPrices(newPrices);
      updateParent(selectedProductsRef.current, newNewProducts, newQuantities, newPrices, newStorage);
    } else {
      // Remove existing product
      const newSelected = selectedProductsRef.current.filter((p, i) => getProductLineKey(p, i) !== key);
      setSelectedProducts(newSelected);
      const newQuantities = { ...productQuantitiesRef.current };
      const newPrices = { ...productPricesRef.current };
      const newStorage = { ...productStorageLocationsRef.current };
      const newSourceStorage = { ...productSourceStorageLocationsRef.current };
      delete newQuantities[key];
      delete newPrices[key];
      delete newStorage[key];
      delete newSourceStorage[key];
      productStorageLocationsRef.current = newStorage;
      setProductStorageLocations(newStorage);
      productSourceStorageLocationsRef.current = newSourceStorage;
      setProductSourceStorageLocations(newSourceStorage);
      setProductQuantities(newQuantities);
      setProductPrices(newPrices);
      updateParent(newSelected, newProductsRef.current, newQuantities, newPrices, newStorage);
    }
  };

  const handleSplitProductLine = (lineKey: string) => {
    if (!allowDuplicateProducts) return;

    const sourceIndex = selectedProductsRef.current.findIndex((p, i) => getProductLineKey(p, i) === lineKey);
    if (sourceIndex < 0) return;

    const source = selectedProductsRef.current[sourceIndex];
    const duplicated = { ...source, _lineKey: makeLineKey() } as ProductApiResponse;
    const newSelected = [...selectedProductsRef.current, duplicated];
    setSelectedProducts(newSelected);

    const newQuantities = { ...productQuantitiesRef.current, [getProductLineKey(duplicated, newSelected.length - 1)]: 0 };
    const newPrices = { ...productPricesRef.current };
    const newStorage = { ...productStorageLocationsRef.current, [getProductLineKey(duplicated, newSelected.length - 1)]: null };
    const newSourceStorage = { ...productSourceStorageLocationsRef.current, [getProductLineKey(duplicated, newSelected.length - 1)]: null };

    setProductQuantities(newQuantities);
    setProductPrices(newPrices);
    productStorageLocationsRef.current = newStorage;
    setProductStorageLocations(newStorage);
    productSourceStorageLocationsRef.current = newSourceStorage;
    setProductSourceStorageLocations(newSourceStorage);

    updateParent(newSelected, newProductsRef.current, newQuantities, newPrices, newStorage);
  };

  const handleAddNewProduct = () => {
    setNewProductFormData({
      name: '',
      description: '',
      category: [],
      price: '',
      quantity: 1,
      unit_price: '',
      supplier_ids: [],
      label_ids: [],
      shelf_life_days: '',
    });
    setNewProductDialogOpen(true);
  };

  const handleSaveNewProduct = () => {
    const categoriesNonEmpty = newProductFormData.category.filter((c) => c.length > 0);
    if (!newProductFormData.name || categoriesNonEmpty.length === 0 || !newProductFormData.price) {
      return;
    }

    const newProductId = `new-${Date.now()}`;
    const resolvedUnitPrice = showPrice
      ? (newProductFormData.unit_price || newProductFormData.price)
      : newProductFormData.price;

    const shelfLifeValid = dateInputToDays(newProductFormData.shelf_life_days);

    const newProduct = {
      id: newProductId,
      product: {
        name: newProductFormData.name,
        description: newProductFormData.description || undefined,
        category: categoriesNonEmpty,
        price: newProductFormData.price,
        ...(newProductFormData.supplier_ids.length > 0 && { supplier_ids: newProductFormData.supplier_ids }),
        ...(newProductFormData.label_ids.length > 0 && { label_ids: newProductFormData.label_ids }),
        ...(shelfLifeValid !== undefined && { shelf_life_days: shelfLifeValid }),
      },
      quantity: newProductFormData.quantity,
      unit_price: resolvedUnitPrice,
    };

    const updatedNewProducts = [...newProductsRef.current, newProduct];
    setNewProducts(updatedNewProducts);

    const newQuantities = { ...productQuantitiesRef.current, [newProductId]: newProductFormData.quantity };
    const newPrices = { ...productPricesRef.current, [newProductId]: resolvedUnitPrice };
    const newStorage = requireStorageLocation
      ? { ...productStorageLocationsRef.current, [newProductId]: null }
      : productStorageLocationsRef.current;
    if (requireStorageLocation) {
      productStorageLocationsRef.current = newStorage;
      setProductStorageLocations(newStorage);
    }
    setProductQuantities(newQuantities);
    setProductPrices(newPrices);

    updateParent(selectedProductsRef.current, updatedNewProducts, newQuantities, newPrices, newStorage);

    setNewProductDialogOpen(false);
    setNewProductFormData({
      name: '',
      description: '',
      category: [],
      price: '',
      quantity: 1,
      unit_price: '',
      supplier_ids: [],
      label_ids: [],
      shelf_life_days: '',
    });
  };

  // Table columns for product selection
  const tableColumns: Column<ProductApiResponse>[] = React.useMemo(() => [
    {
      id: "name",
      label: t("products.columns.name"),
      flex: 1.5,
    },
    {
      id: "product_code",
      label: t("products.columns.barcode"),
      flex: 1,
      format: (value: string | undefined) => value || "-",
    },
    {
      id: "category",
      label: t("products.columns.category"),
      flex: 1,
      format: (value: string[]) => {
        if (Array.isArray(value) && value.length > 0) {
          return value.join(", ");
        }
        return "-";
      },
    },
    {
      id: "price",
      label: t("products.columns.price"),
      flex: 1,
      format: (value: string) => {
        const numValue = parseFloat(value);
        return isNaN(numValue) ? "-" : `${numValue.toFixed(2)} zł`;
      },
    },
    {
      id: "quantity",
      label: t("products.columns.quantity"),
      flex: 1,
    },
  ], [t]);

  const bundleColumns: Column<BundleDefinition>[] = React.useMemo(() => [
    {
      id: "bundle_product_name",
      label: t("products.columns.name"),
      flex: 1.7,
    },
    {
      id: "bundle_product_code",
      label: t("products.columns.barcode"),
      flex: 1,
      format: (value: string | undefined) => value || "-",
    },
    {
      id: "bundle_product_quantity",
      label: t("products.columns.quantity"),
      flex: 0.8,
      align: "right",
      format: (value: number) => value ?? 0,
    },
  ], [t]);

  // Handle product selection from table
  const handleTableProductSelect = (product: ProductApiResponse, options?: { defaultQuantity?: number }) => {
    // Check if product is already selected
    const isAlreadySelected = selectedProductsRef.current.some(p => p.id === product.id);

    if (isAlreadySelected && !allowDuplicateProducts) {
      setTableSelectorDialogOpen(false);
      setBundleSelectorDialogOpen(false);
      return;
    }

    const lineProduct = allowDuplicateProducts
      ? ({ ...product, _lineKey: makeLineKey() } as ProductApiResponse)
      : product;
    const newSelected = [...selectedProductsRef.current, lineProduct];
    setSelectedProducts(newSelected);

    // Initialize quantities and prices
    const newQuantities = { ...productQuantitiesRef.current };
    const newPrices = { ...productPricesRef.current };
    const key = getProductLineKey(lineProduct, newSelected.length - 1);

    if (!(key in newQuantities)) {
      const defaultQty = options?.defaultQuantity ?? (allowNegativeQuantity && product.is_bundle ? -1 : 1);
      newQuantities[key] = defaultQty;
    }

    if (showPrice && !(key in newPrices)) {
      newPrices[key] = product.price || '0.00';
    }

    let newStorage = productStorageLocationsRef.current;
    if (requireStorageLocation) {
      newStorage = { ...productStorageLocationsRef.current, [key]: null };
      productStorageLocationsRef.current = newStorage;
      setProductStorageLocations(newStorage);
    }
    if (requireSourceStorageLocation) {
      const newSourceStorage = { ...productSourceStorageLocationsRef.current, [key]: null };
      productSourceStorageLocationsRef.current = newSourceStorage;
      setProductSourceStorageLocations(newSourceStorage);
    }

    setProductQuantities(newQuantities);
    setProductPrices(newPrices);

    // Update parent with all current values
    updateParent(newSelected, newProductsRef.current, newQuantities, newPrices, newStorage);

    setTableSelectorDialogOpen(false);
    setBundleSelectorDialogOpen(false);
  };

  const handleBundleSelect = async (bundle: BundleDefinition) => {
    setSelectingBundleId(bundle.id);
    try {
      const product = await productsApi.getProduct(bundle.bundle_product);
      // KOR na pakiecie: intencja to "zdjąć" — ujemna ilość domyślnie.
      handleTableProductSelect(product, { defaultQuantity: allowNegativeQuantity ? -1 : 1 });
    } finally {
      setSelectingBundleId(null);
    }
  };

  return (
    <Box sx={{ width: '100%', mt: 2 }}>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        {useTableSelector ? (
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              onClick={() => setTableSelectorDialogOpen(true)}
              disabled={disabled || warehousesLoading}
              sx={{
                borderColor: "#474747ff",
                color: "text.primary",
                "&:hover": {
                  borderColor: "rgba(255, 255, 255, 0.5)",
                  backgroundColor: "rgba(255, 255, 255, 0.05)",
                },
              }}
            >
              {t("documents.form.selectExistingProduct")}
            </Button>
            {showBundleSelector && (
              <Button
                variant="outlined"
                onClick={() => setBundleSelectorDialogOpen(true)}
                disabled={disabled || warehousesLoading}
                sx={{
                  borderColor: "#474747ff",
                  color: "text.primary",
                  "&:hover": {
                    borderColor: "rgba(255, 255, 255, 0.5)",
                    backgroundColor: "rgba(255, 255, 255, 0.05)",
                  },
                }}
              >
                {t("documents.form.selectExistingBundle")}
              </Button>
            )}
          </Stack>
        ) : (
          <Box sx={{ flex: 1 }}>
            <Autocomplete<ProductApiResponse, true, false, false>
              multiple
              options={products}
              value={selectedProducts}
              onChange={handleProductChange}
              disabled={disabled || isLoading || warehousesLoading}
              loading={isLoading}
              getOptionLabel={(option) => option.name || ''}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={label}
                  error={error}
                  helperText={helperText}
                  required={required}
                  fullWidth
                  margin="normal"
                  sx={getTextFieldStyles({ error })}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.id}>
                  <Typography>{option.name}</Typography>
                  {option.product_code && (
                    <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                      ({option.product_code})
                    </Typography>
                  )}
                </Box>
              )}
              fullWidth
              sx={getAutocompleteStyles()}
              ListboxProps={{
                sx: getAutocompleteListboxStyles(),
              }}
            />
          </Box>
        )}
        {showAddNewButton && (
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddNewProduct}
            disabled={disabled}
            sx={{
              borderColor: "#474747ff",
              color: "text.primary",
              "&:hover": {
                borderColor: "rgba(255, 255, 255, 0.5)",
                backgroundColor: "rgba(255, 255, 255, 0.05)",
              },
            }}
          >
            {t("common.addNew")}
          </Button>
        )}
      </Box>

      {(selectedProducts.length > 0 || newProducts.length > 0) && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
            {t('documents.form.selectedProductsOrBundles', { defaultValue: 'Wybrane produkty/pakiety:' })}
          </Typography>
          {shouldShowStorageLocationHint && (
            <Typography variant="caption" sx={{ display: 'block', mb: 1.5, color: 'text.secondary' }}>
              {storageLocationHint}
            </Typography>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Existing products */}
            {selectedProducts.map((product, rowIndex) => {
              const key = getProductLineKey(product, rowIndex);
              const korOpts = korOptionsByKey.get(key) ?? [];
              const sourceOpts = sourceOptionsByKey.get(key) ?? [];
              const qtyVal = Number(productQuantities[key] ?? 0);
              const locLoading = storageLocationsLoading || !!productLocationQueries[rowIndex]?.isLoading;
              const showNegativeEmpty =
                requireStorageLocation && qtyVal < 0 && !locLoading && korOpts.length === 0;
              const hasSelectedSource = (productSourceStorageLocations[key] ?? null) != null;
              const showSourceSelector =
                requireSourceStorageLocation && (!product.is_bundle || sourceOpts.length > 0 || hasSelectedSource);

              return (
                <Paper
                  key={key}
                  elevation={1}
                  sx={{
                    p: 2,
                    backgroundColor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Stack spacing={2} direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }}>
                    <Box sx={{ flex: showPrice ? 1 : 2, minWidth: 0 }}>
                      <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                        {product.name}
                      </Typography>
                      {product.product_code && (
                        <Typography variant="caption" color="text.secondary">
                          {product.product_code}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ flex: showPrice ? 1 : 2, minWidth: 0 }}>
                      <TextField
                        label="Ilość"
                        type="text"
                        inputMode={allowNegativeQuantity ? "text" : "numeric"}
                        value={productQuantityInputs[key] ?? (productQuantities[key] != null ? String(productQuantities[key]) : '')}
                        onChange={(e) => handleQuantityChange(key, e.target.value, !!product.is_bundle)}
                        onBlur={() => handleQuantityBlur(key, !!product.is_bundle)}
                        disabled={disabled}
                        required
                        fullWidth
                        inputProps={{
                          inputMode: allowNegativeQuantity ? "text" : "numeric",
                          pattern: allowNegativeQuantity ? "-?[0-9]*\\.?[0-9]*" : "[0-9]*\\.?[0-9]*",
                          ...(allowNegativeQuantity && product.is_bundle ? { max: -1 } : {}),
                        }}
                        sx={getTextFieldStyles()}
                      />
                    </Box>
                    {requireStorageLocation && showSourceSelector && (
                      <Box sx={{ flex: 2, minWidth: 0 }}>
                        <Autocomplete<KorStorageOption, false, false, false>
                          options={requireSourceStorageLocation ? sourceOpts : korOpts}
                          value={
                            (requireSourceStorageLocation
                              ? sourceOpts.find((o) => o.id === (productSourceStorageLocations[key] ?? null))
                              : null) ?? null
                          }
                          onChange={(_, opt) => handleSourceStorageLocationChange(key, opt)}
                          getOptionLabel={(o) => o.korLabel}
                          isOptionEqualToValue={(a, b) => a.id === b.id}
                          loading={locLoading}
                          disabled={disabled || warehousesLoading || !requireSourceStorageLocation}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={requireSourceStorageLocation ? 'Skad (lokalizacja zrodlowa)' : ' '}
                              required={requireSourceStorageLocation && !product.is_bundle}
                              fullWidth
                              sx={getTextFieldStyles()}
                            />
                          )}
                          ListboxProps={{ sx: getAutocompleteListboxStyles() }}
                          sx={{ ...getAutocompleteStyles(), width: '100%', display: requireSourceStorageLocation ? 'block' : 'none' }}
                        />
                      </Box>
                    )}
                    {requireStorageLocation && (
                      <Box sx={{ flex: 2, minWidth: 0, display: 'flex', gap: 0.5, alignItems: 'flex-start' }}>
                        <Autocomplete<KorStorageOption, false, false, false>
                          options={korOpts}
                          value={
                            korOpts.find((o) => o.id === (productStorageLocations[key] ?? null)) ?? null
                          }
                          onChange={(_, opt) => handleStorageLocationChange(key, opt)}
                          getOptionLabel={(o) => o.korLabel}
                          isOptionEqualToValue={(a, b) => a.id === b.id}
                          loading={locLoading}
                          disabled={disabled || warehousesLoading}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={requireSourceStorageLocation ? 'Dokad (lokalizacja docelowa)' : t('documents.kor.storageLocation')}
                              required
                              fullWidth
                              helperText={showNegativeEmpty ? t('documents.kor.noStockAnywhere') : undefined}
                              sx={getTextFieldStyles()}
                            />
                          )}
                          ListboxProps={{ sx: getAutocompleteListboxStyles() }}
                          sx={{ ...getAutocompleteStyles(), flex: 1 }}
                        />
                        {requireSourceStorageLocation && (
                          <Tooltip title="Sugeruj lokalizację docelową">
                            <IconButton
                              size="small"
                              disabled={disabled}
                              sx={{ mt: 1, color: '#4caf50' }}
                              onClick={async () => {
                                const qty = productQuantities[key] ?? 1;
                                const warehouseId = selectedWarehouse?.id;
                                if (!warehouseId || !product.id) return;
                                try {
                                  const result = await suggestLocations(product.id, warehouseId, qty);
                                  if (result.auto_suggest_enabled && result.suggestions.length > 0) {
                                    const suggestedId = result.suggestions[0].location_id;
                                    const opt = korOpts.find((o) => o.id === suggestedId);
                                    if (opt) handleStorageLocationChange(key, opt);
                                  }
                                } catch { /* ignore */ }
                              }}
                            >
                              <AutoFixHighIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    )}
                    {showPrice && (
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <TextField
                          label="Cena"
                          type="number"
                          value={productPrices[key] || ''}
                          onChange={(e) => handlePriceChange(key, e.target.value)}
                          disabled={disabled}
                          required
                          fullWidth
                          inputProps={{ min: 0, step: 0.01 }}
                          sx={getTextFieldStyles()}
                        />
                      </Box>
                    )}
                    <Box sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}>
                      {allowDuplicateProducts && (
                        <Tooltip title="Rozdziel na kolejny wiersz">
                          <IconButton
                            onClick={() => handleSplitProductLine(key)}
                            disabled={disabled}
                            color="primary"
                            size="small"
                            sx={{ mr: 0.5 }}
                          >
                            <CallSplitIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <IconButton
                        onClick={() => handleRemoveProduct(key)}
                        disabled={disabled}
                        color="error"
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Stack>
                </Paper>
              );
            })}

            {/* New products */}
            {newProducts.map((newProduct) => {
              const key = newProduct.id;
              const korOpts = korOptionsByKey.get(key) ?? [];
              const qtyVal = Number(productQuantities[key] ?? newProduct.quantity ?? 0);
              const locLoading = storageLocationsLoading;
              const showNegativeEmpty =
                requireStorageLocation && qtyVal < 0 && !locLoading && korOpts.length === 0;

              return (
                <Paper
                  key={key}
                  elevation={1}
                  sx={{
                    p: 2,
                    backgroundColor: 'background.paper',
                    border: '2px solid',
                    borderColor: 'primary.main',
                  }}
                >
                  <Stack spacing={2}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip label="Nowy produkt" size="small" color="primary" />
                    </Box>
                    <Stack spacing={2} direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }}>
                      <Box sx={{ flex: showPrice ? 1 : 2, minWidth: 0 }}>
                        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                          {newProduct.product?.name || ''}
                        </Typography>
                        {newProduct.product?.description && (
                          <Typography variant="caption" color="text.secondary">
                            {newProduct.product.description}
                          </Typography>
                        )}
                        {newProduct.product?.category && newProduct.product.category.length > 0 && (
                          <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {newProduct.product.category.map((cat, idx) => (
                              <Chip key={idx} label={cat} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                            ))}
                          </Box>
                        )}
                      </Box>
                      <Box sx={{ flex: showPrice ? 1 : 2, minWidth: 0 }}>
                        <TextField
                          label="Ilość"
                          type="text"
                          inputMode={allowNegativeQuantity ? "text" : "numeric"}
                          value={productQuantityInputs[key] ?? (productQuantities[key] != null ? String(productQuantities[key]) : (newProduct.quantity != null ? String(newProduct.quantity) : ''))}
                          onChange={(e) => handleQuantityChange(key, e.target.value)}
                          onBlur={() => handleQuantityBlur(key)}
                          disabled={disabled}
                          required
                          fullWidth
                          inputProps={{
                            inputMode: allowNegativeQuantity ? "text" : "numeric",
                            pattern: allowNegativeQuantity ? "-?[0-9]*\\.?[0-9]*" : "[0-9]*\\.?[0-9]*",
                          }}
                          sx={getTextFieldStyles()}
                        />
                      </Box>
                      {requireStorageLocation && (
                        <Box sx={{ flex: 2, minWidth: 0 }}>
                          <Autocomplete<KorStorageOption, false, false, false>
                            options={korOpts}
                            value={
                              korOpts.find((o) => o.id === (productStorageLocations[key] ?? null)) ?? null
                            }
                            onChange={(_, opt) => handleStorageLocationChange(key, opt)}
                            getOptionLabel={(o) => o.korLabel}
                            isOptionEqualToValue={(a, b) => a.id === b.id}
                            loading={locLoading}
                            disabled={disabled || warehousesLoading}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label={t('documents.kor.storageLocation')}
                                required
                                fullWidth
                                helperText={showNegativeEmpty ? t('documents.kor.noStockAnywhere') : undefined}
                                sx={getTextFieldStyles()}
                              />
                            )}
                            ListboxProps={{ sx: getAutocompleteListboxStyles() }}
                            sx={{ ...getAutocompleteStyles(), width: '100%' }}
                          />
                        </Box>
                      )}
                      {showPrice && (
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <TextField
                            label="Cena"
                            type="number"
                            value={productPrices[key] || newProduct.unit_price || (newProduct.product ? String(newProduct.product.price) : '') || ''}
                            onChange={(e) => handlePriceChange(key, e.target.value)}
                            disabled={disabled}
                            required
                            fullWidth
                            inputProps={{ min: 0, step: 0.01 }}
                            sx={getTextFieldStyles()}
                          />
                        </Box>
                      )}
                      <Box sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}>
                        <IconButton
                          onClick={() => handleRemoveProduct(key)}
                          disabled={disabled}
                          color="error"
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Stack>
                  </Stack>
                </Paper>
              );
            })}
          </Box>
        </Box>
      )}

      {/* Dialog for creating new product */}
      <Dialog
        open={newProductDialogOpen}
        onClose={() => setNewProductDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "11px",
            background: "#2a2a2a",
            border: "1px solid #474747ff",
          },
        }}
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" component="div" fontWeight={600}>
              {t("products.addNewProduct")}
            </Typography>
            <IconButton
              edge="end"
              color="inherit"
              onClick={() => setNewProductDialogOpen(false)}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 2 }}>
            <TextField
              label={`${t("products.form.nameRequired")} *`}
              value={newProductFormData.name}
              onChange={(e) => setNewProductFormData((prev) => ({ ...prev, name: e.target.value }))}
              fullWidth
              margin="normal"
              sx={getTextFieldStyles()}
            />
            <TextField
              label={t("products.form.description")}
              value={newProductFormData.description}
              onChange={(e) => setNewProductFormData((prev) => ({ ...prev, description: e.target.value }))}
              multiline
              rows={2}
              fullWidth
              margin="normal"
              sx={getTextFieldStyles()}
            />
            <CategorySelector
              value={newProductFormData.category}
              onChange={(categories) => setNewProductFormData((prev) => ({ ...prev, category: categories }))}
              label={t("products.form.category")}
              required
            />
            <TextField
              label={`${t("products.form.price")} *`}
              type="number"
              value={newProductFormData.price}
              onChange={(e) => {
                const price = e.target.value;
                setNewProductFormData((prev) => {
                  const unitPriceSynced =
                    !showPrice ||
                    prev.unit_price === '' ||
                    prev.unit_price === prev.price;
                  return {
                    ...prev,
                    price,
                    unit_price: unitPriceSynced ? price : prev.unit_price,
                  };
                });
              }}
              fullWidth
              margin="normal"
              inputProps={{ min: 0, step: 0.01 }}
              sx={getTextFieldStyles()}
            />
            {showPrice && (
              <TextField
                label={t("documents.form.productUnitPrice")}
                type="number"
                value={newProductFormData.unit_price || newProductFormData.price}
                onChange={(e) => setNewProductFormData((prev) => ({ ...prev, unit_price: e.target.value }))}
                fullWidth
                margin="normal"
                inputProps={{ min: 0, step: 0.01 }}
                sx={getTextFieldStyles()}
              />
            )}
            <TextField
              label={t("products.form.quantity")}
              type="number"
              value={newProductFormData.quantity}
              onChange={(e) => setNewProductFormData((prev) => ({ ...prev, quantity: parseFloat(e.target.value) || 1 }))}
              fullWidth
              margin="normal"
              inputProps={{ min: 1, step: 1 }}
              sx={getTextFieldStyles()}
            />
            <TextField
              label={t("products.form.initialExpiryDate")}
              helperText={t("products.form.shelfLifeDaysHelp") as string}
              type="date"
              value={newProductFormData.shelf_life_days}
              onChange={(e) => setNewProductFormData((prev) => ({ ...prev, shelf_life_days: e.target.value }))}
              fullWidth
              margin="normal"
              InputLabelProps={{ shrink: true }}
              InputProps={{
                onClick: (e) => {
                  const input = (e.currentTarget as HTMLElement).querySelector('input[type="date"]') as HTMLInputElement | null;
                  input?.showPicker?.();
                  input?.focus();
                },
              }}
              sx={{
                ...getTextFieldStyles(),
                "& .MuiOutlinedInput-root": { cursor: "pointer" },
                "& .MuiInputBase-input": { cursor: "pointer" },
                "& .MuiInputBase-input::-webkit-calendar-picker-indicator": {
                  filter: "invert(1)",
                  cursor: "pointer",
                  opacity: 0.9,
                },
              }}
            />
            <LabelSelector
              value={newProductFormData.label_ids}
              onChange={(labelIds) => setNewProductFormData((prev) => ({ ...prev, label_ids: labelIds }))}
              label={t("products.form.labels")}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0, gap: 1 }}>
          <Button
            onClick={() => setNewProductDialogOpen(false)}
            variant="outlined"
            color="inherit"
            sx={{
              borderColor: "#474747ff",
              color: "text.primary",
              "&:hover": {
                borderColor: "rgba(255, 255, 255, 0.5)",
                backgroundColor: "rgba(255, 255, 255, 0.05)",
              },
            }}
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleSaveNewProduct}
            variant="contained"
            color="primary"
            disabled={!newProductFormData.name || newProductFormData.category.filter((c) => c.length > 0).length === 0 || !newProductFormData.price}
            sx={{
              minWidth: 100,
              "&:disabled": {
                backgroundColor: "rgba(255, 255, 255, 0.12)",
                color: "rgba(255, 255, 255, 0.3)",
              },
            }}
          >
            {t("common.add")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog for selecting existing products from table */}
      <Dialog
        open={tableSelectorDialogOpen}
        onClose={() => setTableSelectorDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "11px",
            background: "#2a2a2a",
            border: "1px solid #474747ff",
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" component="div" fontWeight={600}>
              {t("documents.form.selectExistingProduct")}
            </Typography>
            <IconButton
              edge="end"
              color="inherit"
              onClick={() => setTableSelectorDialogOpen(false)}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: 0, px: 2, '&.MuiDialogContent-root': { paddingTop: 2 } }}>
          <Table<ProductApiResponse>
            columns={tableColumns}
            data={tablePagination.data}
            page={tablePagination.page}
            rowsPerPage={tablePagination.pageSize}
            totalRows={tablePagination.totalCount}
            onPageChange={tablePagination.handlePageChange}
            onRowsPerPageChange={tablePagination.handlePageSizeChange}
            onSearchChange={tablePagination.handleSearchChange}
            rowKey="id"
            onRowClick={handleTableProductSelect}
            loading={tablePagination.isLoading}
            searchable={true}
            searchPlaceholder={t("products.searchPlaceholder")}
            title=""
            description=""
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1, pb: 2 }}>
          <Button
            onClick={() => setTableSelectorDialogOpen(false)}
            variant="outlined"
            sx={{
              borderColor: "#474747ff",
              color: "text.primary",
              "&:hover": {
                borderColor: "rgba(255, 255, 255, 0.5)",
                backgroundColor: "rgba(255, 255, 255, 0.05)",
              },
            }}
          >
            {t("common.cancel")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog for selecting existing bundles */}
      <Dialog
        open={showBundleSelector && bundleSelectorDialogOpen}
        onClose={() => setBundleSelectorDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "11px",
            background: "#2a2a2a",
            border: "1px solid #474747ff",
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" component="div" fontWeight={600}>
              {t("documents.form.selectExistingBundle")}
            </Typography>
            <IconButton
              edge="end"
              color="inherit"
              onClick={() => setBundleSelectorDialogOpen(false)}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: 0, px: 2, '&.MuiDialogContent-root': { paddingTop: 2 } }}>
          <Table<BundleDefinition>
            columns={bundleColumns}
            data={bundleDefinitionsOnStock}
            page={0}
            rowsPerPage={bundleDefinitionsOnStock.length || 10}
            totalRows={bundleDefinitionsOnStock.length}
            onPageChange={() => undefined}
            onRowsPerPageChange={() => undefined}
            rowKey="id"
            onRowClick={(row) => void handleBundleSelect(row)}
            loading={bundleDefinitionsLoading || selectingBundleId !== null}
            searchable={false}
            emptyMessage={t("bundles.noDefinitions")}
            title=""
            description=""
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1, pb: 2 }}>
          <Button
            onClick={() => setBundleSelectorDialogOpen(false)}
            variant="outlined"
            sx={{
              borderColor: "#474747ff",
              color: "text.primary",
              "&:hover": {
                borderColor: "rgba(255, 255, 255, 0.5)",
                backgroundColor: "rgba(255, 255, 255, 0.05)",
              },
            }}
          >
            {t("common.cancel")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductSelector;
